<template>
  <div
    class="reportGraph"
    style="width: 100%;"
  >
    <div
      v-if="['question1a', 'question1b'].includes(scene)"
      class="counterWrap"
    >
      <div class="counterTitle">
        NIN
      </div>
      <template>
        <div
          v-for="(countSelf, key) in ninCount.self"
          :key="`selfNIN${key}`"
          class="ninCounter"
          :class="key === 0 ? 'slotOne' : 'slotTwo'"
        >
          {{ countSelf > 0 ? countSelf : '-' }}
        </div>
      </template>
      <template>
        <div
          v-for="(countManager, key) in ninCount.manager"
          :key="`managerNIN${key}`"
          class="ninCounter"
          :class="key === 0 ? 'slotOne' : 'slotTwo'"
        >
          {{ countManager > 0 ? countManager : '-' }}
        </div>
      </template>
      <template>
        <div
          v-for="(countStak, key) in ninCount.coworkers"
          :key="`stakNIN${key}`"
          class="ninCounter"
          :class="key === 0 ? 'slotOne' : 'slotTwo'"
        >
          {{ countStak > 0 ? countStak : '-' }}
        </div>
      </template>
    </div>

    <div
      ref="chartdiv"
      class="hello"
      :class="scene"
    ></div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themesAnimated);

export default {
  props: {
    role: {
      type: String,
      default: 'leader',
    },

    report: {
      type: Object,
      default: null,
    },

    scene: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      chart: null,
      graph: {
        type: [],
        list: [],
      },
      censored: {
        self: {
          flag: false,
          pulse1: false,
          pulse2: false,
        },
        manager: {
          flag: false,
          pulse1: false,
          pulse2: false,
        },
        coworkers: {
          flag: false,
          pulse1: false,
          pulse2: false,
        },
      },
      nin: {
        self: {
          flag: false,
          pulse1: false,
          pulse2: false,
        },
        manager: {
          flag: false,
          pulse1: false,
          pulse2: false,
        },
        coworkers: {
          flag: false,
          pulse1: false,
          pulse2: false,
        },
      },

      ninCount: {
        self: [],
        manager: [],
        coworkers: [],
      },
    };
  },

  computed: {
    reportCheck() {
      let flag = false;
      if (this.report) {
        flag = true;
      }
      return flag;
    },
  },

  mounted() {
    if (this.reportCheck) {
      this.dataInit();
    }
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },

  methods: {
    dataInit() {
      // Pulse data
      Object.keys(this.report).forEach((pulse) => {
        this.graph.type.push(pulse);
        const short = this.report[pulse][this.scene];
        if (short.stakeholder) {
          short.coworkers = short.stakeholder;
          delete short.stakeholder;
        }

        Object.keys(short).forEach((key) => {
          let unit = {};
          if (short[key] && short[key].nin_count) {
            this.ninCount[key].push(short[key].nin_count);
          } else {
            this.ninCount[key].push(0);
          }

          if (short[key] && short[key].avg === 'CENSORED') {
            unit = {
              role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
              [pulse]: 0,
            };

            this.censored[key].flag = true;
            this.censored[key][pulse] = true;
          } else if (short[key] && short[key].avg === 'NIN' && this.scene !== 'question2') {
            unit = {
              role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
              [pulse]: 0,
            };

            this.nin[key].flag = true;
            this.nin[key][pulse] = true;
          } else if (short[key]) {
            unit = {
              role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
              [pulse]: short[key].avg,
            };
          } else {
            unit = {
              role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
              [pulse]: 0,
            };
          }
          this.graph.list.push(unit);
        });
      });

      this.drawGraph();
    },

    drawGraph() {
      am4core.addLicense(process.env.VUE_APP_AMCHARTS_LICENSE);
      this.chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);
      this.chart.data = this.graph.list;

      // Hide Y axis number
      if (this.scene === 'question2') {
        const format = this.chart.numberFormatter;
        format.numberFormat = '';
      }

      this.createX();
      this.createY();
      this.graph.type.forEach((item, index) => {
        this.createSeries(item, index + 1);
      });
    },

    createX() {
      // Create X axes
      const categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      // // Label
      categoryAxis.dataFields.category = 'role';
      categoryAxis.renderer.labels.template.fill = am4core.color('rgba(0, 0, 0, 0.7)');
      // // Grid
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.grid.template.stroke = am4core.color('rgba(0, 0, 0, 0.7)');
      categoryAxis.renderer.grid.template.strokeOpacity = 0.5;

      // Censored Range background
      Object.keys(this.censored).forEach((role) => {
        if (this.censored[role].flag) {
          // // Style setup
          const range = categoryAxis.axisRanges.create();
          range.axisFill.fill = am4core.color('black');
          range.axisFill.fillOpacity = 0.1;
          range.axisFill.above = true;
          range.label.fillOpacity = 0.5;
          range.label.fill = am4core.color('black');
          range.label.inside = true;
          range.label.valign = 'middle';
          range.label.textAlign = 'middle';
          range.label.fontSize = '11px';

          // // Range
          range.category = `${role.charAt(0).toUpperCase() + role.slice(1)}`;
          range.endCategory = `${role.charAt(0).toUpperCase() + role.slice(1)}`;
          range.label.text = 'Not enough\n responses\n to display';

          if (this.graph.type.length !== 1) {
            if (this.censored[role].pulse1) {
              // // End line
              range.locations.endCategory = 0.5;
            } else if (this.censored[role].pulse2) {
              // // Start line
              range.locations.category = 0.5;
            }
          }
        }
      });

      // NIN Range background
      Object.keys(this.nin).forEach((role) => {
        if (this.nin[role].flag) {
          // // Style setup
          const range = categoryAxis.axisRanges.create();
          range.axisFill.fill = am4core.color('black');
          range.axisFill.fillOpacity = 0.1;
          range.axisFill.above = true;
          range.label.fillOpacity = 0.5;
          range.label.fill = am4core.color('black');
          range.label.inside = true;
          range.label.valign = 'middle';
          range.label.textAlign = 'middle';
          range.label.fontSize = '11px';

          // // Range
          range.category = `${role.charAt(0).toUpperCase() + role.slice(1)}`;
          range.endCategory = `${role.charAt(0).toUpperCase() + role.slice(1)}`;
          range.label.text = 'No\n improvement\n needed';

          if (this.graph.type.length !== 1) {
            if (this.nin[role].pulse1) {
              // // End line
              range.locations.endCategory = 0.5;
            } else if (this.nin[role].pulse2) {
              // // Start line
              range.locations.category = 0.5;
            }
          }
        }
      });
    },

    createY() {
      // Create Y axes
      const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
      // // Label
      valueAxis.renderer.labels.template.fill = am4core.color('rgba(0, 0, 0, 0.7)');

      if (this.scene !== 'question2') {
        // // Grid
        valueAxis.min = -3;
        valueAxis.max = 3;
        valueAxis.renderer.minGridDistance = 40;
      } else {
        // // Grid
        valueAxis.min = 0;
        valueAxis.max = 5;
        valueAxis.renderer.minGridDistance = 70;
      }

      valueAxis.renderer.baseGrid.stroke = am4core.color('rgba(0, 0, 0, 0.7)');
      valueAxis.renderer.baseGrid.strokeOpacity = 0.7;
      valueAxis.renderer.baseGrid.strokeWidth = 1.8;

      valueAxis.renderer.grid.template.stroke = am4core.color('rgba(0, 0, 0, 0.7)');
      valueAxis.renderer.grid.template.strokeOpacity = 0.5;
      valueAxis.renderer.grid.template.strokeDasharray = 3;
    },

    createSeries(pulse, index) {
      // Create series
      const series = this.chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = pulse;
      series.dataFields.categoryX = 'role';
      series.name = `Pulse${index}`;
      series.columns.template.fillOpacity = 0.8;
      series.columns.template.fill = am4core.color(`${index === 1 ? '#00ace2' : '#02be90'}`);

      if (this.scene !== 'question2') {
        series.columns.template.tooltipText = '{categoryX}: [bold]{valueY}[/]';
      } else {
        series.columns.template.tooltipText = '{categoryX}';
      }

      const columnTemplate = series.columns.template;
      columnTemplate.strokeOpacity = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
  .reportGraph {
    .counterWrap {
      display: flex;
      align-items: center;
      margin: 10px 15px 5px 2px;
      box-sizing: border-box;

      .counterTitle {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.7);
        padding: 0 10px;
      }

      .ninCounter {
        width: 100%;
        border: 1px solid #c4c4c4;
        padding: 8px 2px;
        text-align: center;
        font-size: 12px;
        font-weight: bold;

        &:not(:last-child) {
          border-right: none;
        }

        &.slotOne {
          color: #00ace2;
        }

        &.slotTwo {
          color: #02be90;
        }
      }
    }

    .hello {
      width: 100%;
      height: 250px;
      font-size: 12px;
      font-style: normal;

      &.question2 {
        height: 500px;
      }
    }
  }
</style>
